/**
 * VisualLabelCanvas
 *
 * Interactive drag-and-drop canvas for positioning label elements.
 * Elements are rendered directly on a scaled label surface and can be
 * clicked to select, then dragged to reposition. The selected element
 * index is lifted to the parent for the properties panel.
 */
import { Box, Typography } from '@mui/material';
import QRCode from 'qrcode';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePreviewElements } from '../../services/printing';
import type { LabelTemplateElement } from '../../types/printing';

/** Resolve a %-or-dot value to dots. */
function resolveToDots(value: string | number | undefined, totalDots: number): number {
  if (value === undefined || value === '') return 0;
  if (typeof value === 'number') return value;
  const s = String(value).trim();
  if (s.endsWith('%')) {
    return Math.round((parseFloat(s) / 100) * totalDots);
  }
  return parseInt(s, 10) || 0;
}

/** Convert dots back to the original format (keep % if it was %) */
function dotsToOriginalFormat(
  original: string | number | undefined,
  newDots: number,
  totalDots: number
): string | number {
  if (typeof original === 'string' && original.trim().endsWith('%')) {
    return `${Math.round((newDots / totalDots) * 100)}%`;
  }
  return Math.round(newDots);
}

// Mock asset for template preview
const PREVIEW_ASSET = {
  id: 'preview-id',
  customer_id: 'preview-cust',
  tag_id: 'AST-PREVIEW-001',
  serial_number: 'SN-12345-ABCDE',
  type: 'HVAC Unit',
  status: 'active' as const,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  customer: {
    id: 'preview-cust',
    name: 'Acme Corporation',
    email: null,
    phone: null,
    address: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
};

function QRPreview({ data, size }: { data: string; size: number }) {
  const [src, setSrc] = useState<string>('');
  useEffect(() => {
    QRCode.toDataURL(data, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then(setSrc)
      .catch(() => setSrc(''));
  }, [data, size]);

  if (!src) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          border: '1px dashed #999',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          QR
        </Typography>
      </Box>
    );
  }
  return <img src={src} alt="QR" style={{ width: size, height: size }} />;
}

interface VisualLabelCanvasProps {
  elements: LabelTemplateElement[];
  widthDots: number;
  heightDots: number;
  scale?: number;
  selectedIndex: number | null;
  onSelectElement: (index: number | null) => void;
  onMoveElement: (index: number, newX: string | number, newY: string | number) => void;
  templateId?: string;
  templateMeta?: {
    name: string;
    description: string | null;
    rotation: number;
    is_default: boolean;
    created_by: string | null;
    created_at: string;
    updated_at: string;
  };
}

export function VisualLabelCanvas({
  elements,
  widthDots,
  heightDots,
  scale = 0.6,
  selectedIndex,
  onSelectElement,
  onMoveElement,
  templateId,
  templateMeta,
}: VisualLabelCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{
    index: number;
    startMouseX: number;
    startMouseY: number;
    startDotX: number;
    startDotY: number;
  } | null>(null);

  // Generate rendered elements for display
  const renderedElements = generatePreviewElements(
    PREVIEW_ASSET,
    {
      id: templateId || 'preview',
      name: templateMeta?.name || 'Preview',
      description: templateMeta?.description ?? null,
      width_dots: widthDots,
      height_dots: heightDots,
      rotation: templateMeta?.rotation ?? 0,
      elements,
      is_default: templateMeta?.is_default ?? false,
      created_by: templateMeta?.created_by ?? null,
      created_at: templateMeta?.created_at ?? new Date().toISOString(),
      updated_at: templateMeta?.updated_at ?? new Date().toISOString(),
    },
    { companyName: 'DigniCRM' }
  );

  const handleMouseDown = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onSelectElement(index);

      const el = elements[index];
      const opts = (el.options || {}) as Record<string, unknown>;
      const dotX = resolveToDots(opts.x as string | number | undefined, widthDots);
      const dotY = resolveToDots(opts.y as string | number | undefined, heightDots);

      setDragging({
        index,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startDotX: dotX,
        startDotY: dotY,
      });
    },
    [elements, widthDots, heightDots, onSelectElement]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging) return;

      const dx = (e.clientX - dragging.startMouseX) / scale;
      const dy = (e.clientY - dragging.startMouseY) / scale;

      const newDotX = Math.max(0, Math.min(widthDots, Math.round(dragging.startDotX + dx)));
      const newDotY = Math.max(0, Math.min(heightDots, Math.round(dragging.startDotY + dy)));

      const el = elements[dragging.index];
      const opts = (el.options || {}) as Record<string, unknown>;

      const newX = dotsToOriginalFormat(opts.x as string | number | undefined, newDotX, widthDots);
      const newY = dotsToOriginalFormat(opts.y as string | number | undefined, newDotY, heightDots);

      onMoveElement(dragging.index, newX, newY);
    },
    [dragging, scale, widthDots, heightDots, elements, onMoveElement]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === canvasRef.current) {
        onSelectElement(null);
      }
    },
    [onSelectElement]
  );

  /** Get approximate bounding box for a rendered element */
  const getElementBounds = useCallback(
    (index: number) => {
      const rendered = renderedElements[index];
      if (!rendered) return { x: 0, y: 0, w: 40, h: 20 };

      const x = rendered.x * scale;
      const y = rendered.y * scale;
      let w = 40 * scale;
      let h = 20 * scale;

      switch (rendered.type) {
        case 'text':
          w = Math.max(60, (rendered.text?.length || 5) * rendered.fontSize * 0.6) * scale;
          h = rendered.fontSize * 1.2 * scale;
          break;
        case 'fieldblock':
          w = rendered.width * scale;
          h = rendered.fontSize * rendered.max * 1.2 * scale;
          break;
        case 'qrcode':
          w = h = rendered.magnificationFactor * 21 * scale;
          break;
        case 'barcode':
          w = Math.max(80, (rendered.data?.length || 8) * 8) * scale;
          h = rendered.height * scale;
          break;
        case 'box':
        case 'inverse-box':
          w = rendered.width * scale;
          h = rendered.height * scale;
          break;
        case 'line':
          w = rendered.width * scale;
          h = Math.max(4, rendered.thickness * scale);
          break;
      }

      return { x, y, w, h };
    },
    [renderedElements, scale]
  );

  const renderElement = (index: number) => {
    const rendered = renderedElements[index];
    if (!rendered) return null;

    const isSelected = selectedIndex === index;
    const isDragging = dragging?.index === index;
    const bounds = getElementBounds(index);

    // Use natural DOM order (index) for z-index. Only elevate when actively being dragged.
    // This ensures you can always click any element regardless of which is selected.
    const zIndex = isDragging ? 1000 : index + 1;

    return (
      <Box
        key={index}
        onMouseDown={e => handleMouseDown(index, e)}
        sx={{
          position: 'absolute',
          left: bounds.x - 2,
          top: bounds.y - 2,
          width: bounds.w + 4,
          height: bounds.h + 4,
          cursor: isDragging ? 'grabbing' : 'grab',
          border: isSelected ? '2px solid #1976d2' : '2px solid transparent',
          borderRadius: '2px',
          '&:hover': {
            border: isSelected ? '2px solid #1976d2' : '2px dashed #90caf9',
          },
          zIndex,
          userSelect: 'none',
          boxSizing: 'border-box',
        }}
      >
        {/* Render the actual content inside the hitbox */}
        <Box sx={{ pointerEvents: 'none', position: 'relative', left: 2, top: 2 }}>
          {renderElementContent(rendered, scale)}
        </Box>
        {/* Selection handles */}
        {isSelected && (
          <>
            {[
              { top: -4, left: -4 },
              { top: -4, right: -4 },
              { bottom: -4, left: -4 },
              { bottom: -4, right: -4 },
            ].map((pos, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  width: 7,
                  height: 7,
                  bgcolor: '#1976d2',
                  border: '1px solid white',
                  borderRadius: '1px',
                  pointerEvents: 'none',
                  ...pos,
                }}
              />
            ))}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      ref={canvasRef}
      onClick={handleCanvasClick}
      sx={{
        position: 'relative',
        width: widthDots * scale,
        height: heightDots * scale,
        border: '1px solid #ccc',
        backgroundColor: '#fff',
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'default',
        // Grid pattern for visual reference
        backgroundImage: `
          linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)
        `,
        backgroundSize: `${20 * scale}px ${20 * scale}px`,
      }}
    >
      {renderedElements.map((_, index) => renderElement(index))}
    </Box>
  );
}

/** Pure render function — maps a LabelElement to JSX (no interactivity) */
function renderElementContent(element: import('../../types/printing').LabelElement, scale: number) {
  switch (element.type) {
    case 'text':
      return (
        <Typography
          sx={{
            fontSize: element.fontSize * scale,
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            lineHeight: 1,
          }}
        >
          {element.text}
        </Typography>
      );

    case 'fieldblock':
      return (
        <Box
          sx={{
            width: element.width * scale,
            fontSize: element.fontSize * scale,
            fontFamily: 'monospace',
            textAlign:
              element.alignment === 'C' ? 'center' : element.alignment === 'R' ? 'right' : 'left',
            overflow: 'hidden',
            wordBreak: 'break-word',
            lineHeight: 1.2,
          }}
        >
          {element.data}
        </Box>
      );

    case 'qrcode':
      return <QRPreview data={element.data} size={element.magnificationFactor * 21 * scale} />;

    case 'barcode':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              height: element.height * scale * 0.7,
              width: Math.max(80, element.data.length * 8) * scale,
              background:
                'repeating-linear-gradient(90deg, #000 0px, #000 2px, #fff 2px, #fff 4px)',
            }}
          />
          {element.humanReadable === 'Y' && (
            <Typography
              variant="caption"
              sx={{ fontSize: 8 * scale, fontFamily: 'monospace', mt: 0.25 }}
            >
              {element.data}
            </Typography>
          )}
        </Box>
      );

    case 'box':
      return (
        <Box
          sx={{
            width: element.width * scale,
            height: element.height * scale,
            border: `${Math.max(1, element.borderThickness * scale)}px solid ${
              element.borderColor === 'W' ? '#fff' : '#000'
            }`,
            borderRadius: element.cornerRounding * scale,
            boxSizing: 'border-box',
          }}
        />
      );

    case 'line':
      return (
        <Box
          sx={{
            width: element.width * scale,
            height: Math.max(1, element.thickness * scale),
            backgroundColor: '#000',
          }}
        />
      );

    case 'inverse-box':
      return (
        <Box
          sx={{
            width: element.width * scale,
            height: element.height * scale,
            backgroundColor: '#000',
            borderRadius: element.cornerRounding * scale,
          }}
        />
      );

    case 'image':
      return (
        <Box
          sx={{
            width: 40 * scale,
            height: 40 * scale,
            border: '1px dashed #999',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 8 * scale }}>
            IMG
          </Typography>
        </Box>
      );

    default:
      return null;
  }
}
